import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import {
  applySchedulePattern,
  fetchWeekSchedule,
  saveScheduleSettings,
  saveWeekSchedule,
  UnauthorizedError,
  type ScheduleDay,
  type ScheduleDayBlock,
} from "@/lib/clinician-api";
import { listStaff } from "@/lib/staff-api";
import {
  datesText,
  nextWindow,
  parseSlotInterval,
  parseSlotMinutes,
  patternFromWeek,
  sortWindows,
  stockholmMondayIso,
  windowsError,
} from "@/lib/schedule-windows";
import { stockholmToday } from "@/lib/onboarding-funnel";
import {
  BOOKING_HORIZON_DAYS,
  MAX_SLOT_INTERVAL_MINUTES,
  MAX_SLOT_MINUTES,
  MIN_SLOT_MINUTES,
  SCHEDULE_EDITABLE_WEEKS,
  SLOT_MINUTES_STEP,
  type SchedulePatternWeekday,
  type StaffMember,
} from "@care-by-ella/contracts";

// TES-756 — the schedule is edited ONE WEEK AT A TIME, because Kirill's weeks
// differ from each other. The old view was a weekly pattern (one row per
// weekday) repeating forever: the only way to remove a single day was to
// remove it every week.
//
// TES-1238 — a day holds SEVERAL windows (Kirill: Tuesdays 09–12 and 13–16).
// The row used to hold one start/end pair, so the second window was never
// saved. A weekly pattern can also be written over a date range in one go,
// and admin sets the doctor's per-hour meeting cap here.
//
// A week with no slots is a valid answer — that week simply cannot be booked.
// Hence the warning at the top when the booking horizon (BOOKING_HORIZON_DAYS) is
// uncovered: the silence should be visible, not discovered by a patient.

const WEEKDAY_LABELS = [
  "Måndag",
  "Tisdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lördag",
  "Söndag",
];

// 06:00..22:00 in half-hour steps — the window a clinic plausibly offers.
const TIME_OPTIONS = (() => {
  const out: { minute: number; label: string }[] = [];
  for (let m = 360; m <= 1320; m += 30) {
    const h = String(Math.floor(m / 60)).padStart(2, "0");
    const mm = String(m % 60).padStart(2, "0");
    out.push({ minute: m, label: `${h}:${mm}` });
  }
  return out;
})();

// The cap choices: none, or 1–6 meetings an hour.
const CAP_OPTIONS = [1, 2, 3, 4, 5, 6];

// All date arithmetic goes through UTC midnight and ISO strings. Local getters
// would move the date by a day for anyone sitting in a zone other than the
// clinic's.
function isoToUtc(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function addDaysIso(iso: string, days: number): string {
  return new Date(isoToUtc(iso).getTime() + days * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

function humanDate(iso: string): string {
  return new Intl.DateTimeFormat("sv-SE", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(isoToUtc(iso));
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// One day's windows: a start and an end per window, "Ta bort" on each, and
// "Lägg till tid". No windows reads "Ledig" — free means no bookable time.
function WindowsEditor({
  blocks,
  onChange,
}: {
  blocks: ScheduleDayBlock[];
  onChange: (next: ScheduleDayBlock[]) => void;
}) {
  const error = windowsError(blocks);
  const patch = (i: number, partial: Partial<ScheduleDayBlock>) =>
    onChange(blocks.map((b, j) => (j === i ? { ...b, ...partial } : b)));
  const timeSelect = (
    value: number,
    onPick: (minute: number) => void,
    label: string,
  ) => (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onPick(Number(e.target.value))}
      className={`rounded-md border bg-background px-sm py-xs ${
        error ? "border-danger" : "border-surfaceMuted"
      }`}
    >
      {TIME_OPTIONS.map((o) => (
        <option key={o.minute} value={o.minute}>
          {o.label}
        </option>
      ))}
    </select>
  );
  return (
    <div className="flex flex-col gap-xs text-sm">
      {blocks.length === 0 ? (
        <span className="text-textSecondary">Ledig</span>
      ) : (
        blocks.map((b, i) => (
          <div key={i} className="flex items-center gap-sm flex-wrap">
            {timeSelect(
              b.startMinute,
              (m) => patch(i, { startMinute: m }),
              "Starttid",
            )}
            <span className="text-textSecondary">–</span>
            {timeSelect(
              b.endMinute,
              (m) => patch(i, { endMinute: m }),
              "Sluttid",
            )}
            <button
              type="button"
              onClick={() => onChange(blocks.filter((_, j) => j !== i))}
              className="text-textSecondary text-xs underline"
            >
              Ta bort
            </button>
          </div>
        ))
      )}
      <div className="flex items-center gap-sm flex-wrap">
        <button
          type="button"
          onClick={() => onChange([...blocks, nextWindow(blocks)])}
          disabled={blocks.length >= 6}
          className="text-brand text-xs underline disabled:opacity-40"
        >
          + Lägg till tid
        </button>
        {error ? <span className="text-danger text-xs">{error}</span> : null}
      </div>
    </div>
  );
}

export function ScheduleRoute() {
  const { signOut, claims } = useAuth();
  // TES-756 — an admin has no schedule of their own to fill in (they take no
  // meetings) and gets a 403 on `me/schedule`, which is doctor-gated. Instead
  // they pick WHICH doctor they are filling in for, and the write goes through
  // the admin-gated `clinicians/:employeeId/schedule`. A doctor, as before,
  // sees only their own and never touches the picker.
  // TES-1089 — a coach with the admin grant reaches this page only as an
  // admin (coaches have no schedule here), so she gets the picker too.
  const isAdmin =
    claims?.role === "admin" || Boolean(claims?.roles?.includes("admin"));
  const [doctors, setDoctors] = useState<StaffMember[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [doctorsError, setDoctorsError] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState(stockholmMondayIso());
  const [days, setDays] = useState<ScheduleDay[]>([]);
  const [slotMinutes, setSlotMinutes] = useState(20);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Bumped after every write, so the horizon warning reads the schedule again.
  const [writes, setWrites] = useState(0);
  // Does the booking horizon cover any slots at all? Read per week and
  // merged, because the horizon spans several weeks.
  const [horizonCovered, setHorizonCovered] = useState<boolean | null>(null);

  // TES-1238 — the per-hour cap, as stored and as being edited.
  const [cap, setCap] = useState<number | null>(null);
  const [capDraft, setCapDraft] = useState<number | null>(null);
  const [capSaving, setCapSaving] = useState(false);
  const [capSaved, setCapSaved] = useState(false);
  // TES-1261 — minutes between bookable start times, stored (null = the
  // meeting length) and as typed (empty = null).
  const [slotInterval, setSlotInterval] = useState<number | null>(null);
  const [intervalDraft, setIntervalDraft] = useState("");
  // TES-1409 — the meeting length as typed; `slotMinutes` above is the stored
  // one.
  const [slotMinutesDraft, setSlotMinutesDraft] = useState("20");

  // TES-1238 — the weekly pattern over a date range.
  const [patternFrom, setPatternFrom] = useState("");
  const [patternTo, setPatternTo] = useState("");
  const [pattern, setPattern] = useState<SchedulePatternWeekday[]>([]);
  const [patternBusy, setPatternBusy] = useState(false);
  const [patternConflicts, setPatternConflicts] = useState<string[] | null>(
    null,
  );
  const [patternResult, setPatternResult] = useState<string | null>(null);

  const firstMonday = stockholmMondayIso();
  const lastMonday = addDaysIso(firstMonday, (SCHEDULE_EDITABLE_WEEKS - 1) * 7);
  const lastEditable = addDaysIso(lastMonday, 6);

  // An admin reads/writes the selected doctor's schedule; a doctor their own
  // (employeeId = undefined → `me/schedule`).
  const targetId = isAdmin ? selectedDoctorId : null;

  const handle = useCallback(
    (e: unknown, set: (msg: string) => void) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      set(errorText(e));
    },
    [signOut],
  );

  const loadWeek = useCallback(
    async (iso: string) => {
      // An admin with no doctor selected has nothing to fetch — and
      // `me/schedule` is doctor-gated, so asking anyway would have returned a
      // 403 and logged them out through the UnauthorizedError path.
      if (isAdmin && !targetId) {
        setDays([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setSaved(false);
      try {
        const week = await fetchWeekSchedule(iso, targetId ?? undefined);
        setSlotMinutes(week.slotMinutes);
        setSlotMinutesDraft(String(week.slotMinutes));
        setDays(week.days);
        setCap(week.maxMeetingsPerHour ?? null);
        setCapDraft(week.maxMeetingsPerHour ?? null);
        setSlotInterval(week.slotIntervalMinutes ?? null);
        setIntervalDraft(String(week.slotIntervalMinutes ?? ""));
      } catch (e) {
        handle(e, setError);
      } finally {
        setLoading(false);
      }
    },
    [handle, isAdmin, targetId],
  );

  useEffect(() => {
    void loadWeek(weekStart);
  }, [weekStart, loadWeek]);

  // Another doctor is another schedule: the pattern and its answers go.
  useEffect(() => {
    setPattern([]);
    setPatternConflicts(null);
    setPatternResult(null);
    setCapSaved(false);
  }, [targetId]);

  // TES-756 — the admin's list of doctors. Fetched once; a doctor does not
  // need it.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const { rows } = await listStaff();
        if (cancelled) return;
        // Only doctors can take meetings, and a deactivated row is filtered
        // out by listSlots anyway — offering them here would have produced a
        // schedule no patient can book.
        setDoctors(
          rows.filter((r) => r.role === "doctor" && r.deactivatedAt === null),
        );
      } catch (e) {
        if (!cancelled) setDoctorsError(errorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  // The booking horizon (BOOKING_HORIZON_DAYS) starts today, not on Monday,
  // so it spans one week more than it has whole weeks. Every week it touches
  // is read, independently of whichever week happens to be displayed.
  useEffect(() => {
    // No doctor selected → no horizon to warn about, and `me/schedule` would
    // have 403'd for an admin.
    if (isAdmin && !targetId) {
      setHorizonCovered(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const weekCount = Math.ceil(BOOKING_HORIZON_DAYS / 7) + 1;
        const weeks = [];
        for (let i = 0; i < weekCount; i++) {
          weeks.push(
            await fetchWeekSchedule(
              addDaysIso(firstMonday, 7 * i),
              targetId ?? undefined,
            ),
          );
        }
        if (cancelled) return;
        const today = stockholmToday();
        const horizonEnd = addDaysIso(today, BOOKING_HORIZON_DAYS);
        const covered = weeks
          .flatMap((w) => w.days)
          .some(
            (d) =>
              d.date >= today && d.date <= horizonEnd && d.blocks.length > 0,
          );
        setHorizonCovered(covered);
      } catch {
        // The warning is a help, not a gate — if it fails, nothing is shown.
        if (!cancelled) setHorizonCovered(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firstMonday, writes, targetId, isAdmin]);

  function setDayBlocks(date: string, blocks: ScheduleDayBlock[]) {
    setSaved(false);
    setDays((prev) =>
      prev.map((d) => (d.date === date ? { ...d, blocks } : d)),
    );
  }

  const invalidDay = useMemo(
    () => days.some((d) => windowsError(d.blocks) !== null),
    [days],
  );

  async function onSave() {
    if (invalidDay || saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Every day is sent with ALL its windows: the API replaces a sent
      // day's windows with exactly these.
      const updated = await saveWeekSchedule(
        {
          weekStart,
          days: days.map((d) => ({
            date: d.date,
            blocks: sortWindows(d.blocks),
          })),
        },
        targetId ?? undefined,
      );
      setDays(updated.days);
      setSlotMinutes(updated.slotMinutes);
      setSaved(true);
      setWrites((n) => n + 1);
    } catch (e) {
      handle(e, setError);
    } finally {
      setSaving(false);
    }
  }

  // TES-1409 — the interval is checked against the length being typed, so an
  // empty interval ("same as the meeting") and its lower bound follow the new
  // length before it is saved. An invalid length falls back to the stored one.
  // The stored length is never an error itself, even one from before the
  // 10–60 rule, so it cannot block saving the cap or the interval.
  const slotMinutesParsed =
    slotMinutesDraft.trim() === String(slotMinutes)
      ? { value: slotMinutes }
      : parseSlotMinutes(slotMinutesDraft);
  const slotMinutesInputError =
    "error" in slotMinutesParsed ? slotMinutesParsed.error : null;
  const effectiveSlotMinutes =
    "value" in slotMinutesParsed ? slotMinutesParsed.value : slotMinutes;
  const slotMinutesChanged =
    "value" in slotMinutesParsed && slotMinutesParsed.value !== slotMinutes;
  const intervalParsed = parseSlotInterval(intervalDraft, effectiveSlotMinutes);
  const intervalError = "error" in intervalParsed ? intervalParsed.error : null;
  const settingsChanged =
    capDraft !== cap ||
    slotMinutesChanged ||
    ("value" in intervalParsed && intervalParsed.value !== slotInterval);

  async function onSaveCap() {
    if (
      !targetId ||
      capSaving ||
      !("value" in intervalParsed) ||
      !("value" in slotMinutesParsed)
    ) {
      return;
    }
    setCapSaving(true);
    setCapSaved(false);
    setError(null);
    try {
      const res = await saveScheduleSettings(targetId, {
        maxMeetingsPerHour: capDraft,
        slotIntervalMinutes: intervalParsed.value,
        // Sent only when changed, for the same reason.
        ...(slotMinutesChanged ? { slotMinutes: slotMinutesParsed.value } : {}),
      });
      setCap(res.maxMeetingsPerHour);
      setCapDraft(res.maxMeetingsPerHour);
      setSlotInterval(res.slotIntervalMinutes);
      setIntervalDraft(String(res.slotIntervalMinutes ?? ""));
      setSlotMinutes(res.slotMinutes);
      setSlotMinutesDraft(String(res.slotMinutes));
      setCapSaved(true);
    } catch (e) {
      handle(e, setError);
    } finally {
      setCapSaving(false);
    }
  }

  function startPatternFromWeek() {
    setPattern(patternFromWeek(days));
    const from = days[0]?.date ?? weekStart;
    setPatternFrom((prev) => prev || from);
    setPatternTo((prev) => prev || addDaysIso(from, 4 * 7 - 1));
    setPatternConflicts(null);
    setPatternResult(null);
  }

  function setPatternDay(weekday: number, blocks: ScheduleDayBlock[]) {
    setPatternResult(null);
    setPatternConflicts(null);
    setPattern((prev) => {
      const rest = prev.filter((p) => p.weekday !== weekday);
      return [...rest, { weekday, blocks }].sort(
        (a, b) => a.weekday - b.weekday,
      );
    });
  }

  const patternInvalid =
    !patternFrom ||
    !patternTo ||
    patternTo < patternFrom ||
    patternTo > lastEditable ||
    pattern.length === 0 ||
    pattern.some((p) => windowsError(p.blocks) !== null);

  async function onApplyPattern(overwrite: boolean) {
    if (patternInvalid || patternBusy) return;
    setPatternBusy(true);
    setPatternResult(null);
    setError(null);
    try {
      const res = await applySchedulePattern(
        {
          from: patternFrom,
          to: patternTo,
          overwrite,
          weekdays: pattern.map((p) => ({
            weekday: p.weekday,
            blocks: sortWindows(p.blocks),
          })),
        },
        targetId ?? undefined,
      );
      setPatternConflicts(res.conflicts.length ? res.conflicts : null);
      setPatternResult(
        `Mönstret lades in på ${res.applied.length} dagar.` +
          (res.unchanged.length
            ? ` ${res.unchanged.length} dagar hade redan samma tider.`
            : ""),
      );
      setWrites((n) => n + 1);
      await loadWeek(weekStart);
    } catch (e) {
      handle(e, setError);
    } finally {
      setPatternBusy(false);
    }
  }

  const weekLabel = `${humanDate(weekStart)} – ${humanDate(addDaysIso(weekStart, 6))}`;
  const canGoBack = weekStart > firstMonday;
  const canGoForward = weekStart < lastMonday;
  const noTarget = isAdmin && !targetId;

  return (
    <div className="flex flex-col gap-md max-w-2xl">
      <div>
        <h1 className="font-display text-3xl tracking-tight">
          {isAdmin ? "Läkarnas scheman" : "Mitt schema"}
        </h1>
        <p className="text-textSecondary mt-xs">
          {isAdmin
            ? "Fyll i en läkares tillgängliga tider — en vecka i taget. Veckor som inte fylls i har inga bokningsbara tider. Patienter kan boka de kommande fyra veckorna."
            : "Välj vilka tider du är tillgänglig för läkarsamtal — en vecka i taget. Veckor du inte fyller i har inga bokningsbara tider. Patienter kan boka de kommande fyra veckorna."}
        </p>
      </div>

      {isAdmin ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm text-sm">
          <div className="flex items-center gap-sm flex-wrap">
            <label htmlFor="doctor" className="text-textSecondary">
              Läkare
            </label>
            <select
              id="doctor"
              value={selectedDoctorId ?? ""}
              onChange={(e) => {
                setSaved(false);
                setSelectedDoctorId(e.target.value || null);
              }}
              className="rounded-md border border-surfaceMuted bg-background px-sm py-xs"
            >
              <option value="">Välj läkare…</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.displayName}
                </option>
              ))}
            </select>
            {doctorsError ? (
              <span className="text-danger text-xs">
                Kunde inte hämta läkarlistan: {doctorsError}
              </span>
            ) : null}
            {!doctorsError && doctors.length === 0 ? (
              <span className="text-textSecondary text-xs">
                Ingen aktiv läkare i kliniken.
              </span>
            ) : null}
          </div>

          {targetId && !loading ? (
            <div className="flex items-center gap-sm flex-wrap">
              <label htmlFor="cap" className="text-textSecondary">
                Max antal möten per timme
              </label>
              <select
                id="cap"
                value={capDraft ?? ""}
                onChange={(e) => {
                  setCapSaved(false);
                  setCapDraft(e.target.value ? Number(e.target.value) : null);
                }}
                className="rounded-md border border-surfaceMuted bg-background px-sm py-xs"
              >
                <option value="">Obegränsat</option>
                {CAP_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <label htmlFor="slot-minutes" className="text-textSecondary">
                Möteslängd (minuter)
              </label>
              <input
                id="slot-minutes"
                type="number"
                inputMode="numeric"
                min={MIN_SLOT_MINUTES}
                max={MAX_SLOT_MINUTES}
                step={SLOT_MINUTES_STEP}
                value={slotMinutesDraft}
                aria-describedby="slot-minutes-hint"
                aria-invalid={slotMinutesInputError ? true : undefined}
                onChange={(e) => {
                  setCapSaved(false);
                  setSlotMinutesDraft(e.target.value);
                }}
                className="w-20 rounded-md border border-surfaceMuted bg-background px-sm py-xs"
              />
              <span
                id="slot-minutes-hint"
                className={
                  slotMinutesInputError
                    ? "text-danger text-xs"
                    : "text-textSecondary text-xs"
                }
              >
                {slotMinutesInputError ?? "Gäller nya bokningar."}
              </span>
              <label htmlFor="slot-interval" className="text-textSecondary">
                Tid mellan bokningsbara starttider (minuter)
              </label>
              <input
                id="slot-interval"
                type="number"
                inputMode="numeric"
                min={effectiveSlotMinutes}
                max={MAX_SLOT_INTERVAL_MINUTES}
                step={5}
                value={intervalDraft}
                placeholder={String(effectiveSlotMinutes)}
                aria-describedby="slot-interval-hint"
                aria-invalid={intervalError ? true : undefined}
                onChange={(e) => {
                  setCapSaved(false);
                  setIntervalDraft(e.target.value);
                }}
                className="w-20 rounded-md border border-surfaceMuted bg-background px-sm py-xs"
              />
              <span
                id="slot-interval-hint"
                className={
                  intervalError
                    ? "text-danger text-xs"
                    : "text-textSecondary text-xs"
                }
              >
                {intervalError ?? "Tom = samma som mötets längd"}
              </span>
              <button
                type="button"
                onClick={onSaveCap}
                disabled={
                  capSaving ||
                  !settingsChanged ||
                  intervalError !== null ||
                  slotMinutesInputError !== null
                }
                className="px-md py-xs rounded-md border border-surfaceMuted disabled:opacity-40"
              >
                {capSaving ? "Sparar…" : "Spara"}
              </button>
              {capSaved ? (
                <span className="text-success text-xs">Sparat ✓</span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {horizonCovered === false ? (
        <div className="bg-warning/10 border border-warning/30 rounded-md p-md text-sm">
          Det finns inga tider alls de kommande {BOOKING_HORIZON_DAYS} dagarna —
          ingen patient kan boka ett läkarsamtal just nu.
        </div>
      ) : null}

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      <div className="bg-surface border border-surfaceMuted rounded-md p-md flex items-center justify-between gap-sm text-sm">
        <button
          type="button"
          onClick={() => setWeekStart(addDaysIso(weekStart, -7))}
          disabled={!canGoBack}
          className="px-md py-xs rounded-md border border-surfaceMuted disabled:opacity-40"
        >
          ← Föregående
        </button>
        <div className="text-center">
          <div className="font-medium">{weekLabel}</div>
          <div className="text-textSecondary text-xs">
            Möteslängd {slotMinutes} min
          </div>
        </div>
        <button
          type="button"
          onClick={() => setWeekStart(addDaysIso(weekStart, 7))}
          disabled={!canGoForward}
          className="px-md py-xs rounded-md border border-surfaceMuted disabled:opacity-40"
        >
          Nästa →
        </button>
      </div>

      {noTarget ? (
        <p className="text-textSecondary">
          Välj en läkare ovan för att se och fylla i hennes eller hans veckor.
        </p>
      ) : loading ? (
        <p className="text-textSecondary">Hämtar…</p>
      ) : (
        <div className="bg-surface border border-surfaceMuted rounded-md divide-y divide-surfaceMuted">
          {days.map((d, i) => (
            <div
              key={d.date}
              className="flex items-start gap-md px-md py-sm flex-wrap"
            >
              <div className="flex items-center gap-sm w-44 pt-xs">
                <span className="font-medium">{WEEKDAY_LABELS[i]}</span>
                <span className="text-textSecondary text-xs">
                  {humanDate(d.date)}
                </span>
              </div>
              <WindowsEditor
                blocks={d.blocks}
                onChange={(blocks) => setDayBlocks(d.date, blocks)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-md">
        <button
          type="button"
          onClick={onSave}
          disabled={saving || loading || invalidDay || noTarget}
          className="px-lg py-sm rounded-md bg-brand text-textOnBrand text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Sparar…" : "Spara veckan"}
        </button>
        {saved ? <span className="text-success text-sm">Sparat ✓</span> : null}
      </div>

      {noTarget || loading ? null : (
        <details className="bg-surface border border-surfaceMuted rounded-md p-md text-sm">
          <summary className="cursor-pointer font-medium">
            Upprepa ett veckomönster
          </summary>
          <div className="flex flex-col gap-sm mt-sm">
            <button
              type="button"
              onClick={startPatternFromWeek}
              className="self-start px-md py-xs rounded-md border border-surfaceMuted"
            >
              Använd veckan ovan som mönster
            </button>
            <div className="flex items-center gap-sm flex-wrap">
              <label htmlFor="pattern-from" className="text-textSecondary">
                Från
              </label>
              <input
                id="pattern-from"
                type="date"
                value={patternFrom}
                min={firstMonday}
                max={lastEditable}
                onChange={(e) => {
                  setPatternConflicts(null);
                  setPatternFrom(e.target.value);
                }}
                className="rounded-md border border-surfaceMuted bg-background px-sm py-xs"
              />
              <label htmlFor="pattern-to" className="text-textSecondary">
                Till
              </label>
              <input
                id="pattern-to"
                type="date"
                value={patternTo}
                min={patternFrom || firstMonday}
                max={lastEditable}
                onChange={(e) => {
                  setPatternConflicts(null);
                  setPatternTo(e.target.value);
                }}
                className="rounded-md border border-surfaceMuted bg-background px-sm py-xs"
              />
            </div>
            <div className="divide-y divide-surfaceMuted border border-surfaceMuted rounded-md">
              {WEEKDAY_LABELS.map((label, i) => {
                const weekday = i + 1;
                const entry = pattern.find((p) => p.weekday === weekday);
                return (
                  <div
                    key={weekday}
                    className="flex items-start gap-md px-md py-sm flex-wrap"
                  >
                    <span className="font-medium w-44 pt-xs">{label}</span>
                    {entry ? (
                      <WindowsEditor
                        blocks={entry.blocks}
                        onChange={(blocks) => setPatternDay(weekday, blocks)}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPatternDay(weekday, [nextWindow([])])}
                        className="text-brand text-xs underline pt-xs"
                      >
                        + Lägg till tid
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {patternConflicts ? (
              <div className="bg-warning/10 border border-warning/30 rounded-md p-md flex flex-col gap-sm">
                <span>
                  {patternConflicts.length} dagar har redan andra tider:{" "}
                  {datesText(patternConflicts)}. Skriv över dem?
                </span>
                <div className="flex gap-sm">
                  <button
                    type="button"
                    onClick={() => onApplyPattern(true)}
                    disabled={patternBusy}
                    className="px-md py-xs rounded-md bg-brand text-textOnBrand disabled:opacity-50"
                  >
                    Skriv över
                  </button>
                  <button
                    type="button"
                    onClick={() => setPatternConflicts(null)}
                    className="px-md py-xs rounded-md border border-surfaceMuted"
                  >
                    Behåll dem
                  </button>
                </div>
              </div>
            ) : null}
            <div className="flex items-center gap-md flex-wrap">
              <button
                type="button"
                onClick={() => onApplyPattern(false)}
                disabled={patternInvalid || patternBusy}
                className="px-lg py-sm rounded-md bg-brand text-textOnBrand text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {patternBusy ? "Sparar…" : "Lägg in mönstret"}
              </button>
              {patternResult ? (
                <span className="text-success text-sm">{patternResult}</span>
              ) : null}
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
